import { useEffect, useMemo } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import {
  ApiClientProvider,
  PermissionsProvider,
  RealtimeProvider,
  usePermissions,
} from '@klareospace/pharos-lib-front-utils';
import type { PermissionsContextValue } from '@klareospace/pharos-lib-front-utils';
import { getPharosTokenClaims, usePharosAuth } from '@klareospace/pharos-lib-front-utils/auth';
import type { PharosJWTToken } from '@klareospace/pharos-lib-types';
import {
  createTelemetryDefaultErrorPage,
  DefaultNotFoundPage,
  DefaultPendingPage,
  PendoBridge,
  subscribeTelemetryPageViews,
  TelemetryBridge,
  TelemetryErrorBoundary,
} from '@klareospace/pharos-lib-web-utils';

import { AuthProvider, useAuth } from './auth';
import { env } from './config';
import { apiConfigs, apiLogger, CORRELATION_ID_PREFIX, setTokenProvider, tokenProvider } from './lib/api-client';
import { pendoClient } from './lib/pendo';
import { queryClient } from './lib/query-client';
import { telemetryClient } from './lib/telemetry';
import { routeTree } from './routeTree.gen';

const REALTIME_NEGOTIATE_URL = '/realtime/negotiate';

const AppDefaultErrorPage = createTelemetryDefaultErrorPage(telemetryClient, env.isDev);

function createAppRouter(): ReturnType<typeof createRouter> {
  return createRouter({
    basepath: '/frontoffice',
    // Initial context — will be updated by InnerApp with real auth/permissions state.
    // Cast is intentional: TanStack Router requires a context value at creation time,
    // but the real objects are provided via RouterProvider before any route renders.
    context: {
      auth: undefined as unknown as ReturnType<typeof useAuth>,
      permissions: undefined as unknown as PermissionsContextValue,
    },
    defaultErrorComponent: AppDefaultErrorPage,
    defaultNotFoundComponent: DefaultNotFoundPage,
    defaultPendingComponent: DefaultPendingPage,
    routeTree,
  });
}

const router = createAppRouter();

// Track page views on every resolved navigation.
subscribeTelemetryPageViews(router, telemetryClient);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/** Bridges auth context token and activity notification into the API client */
function AuthApiClientBridge({ children }: React.PropsWithChildren): React.JSX.Element {
  const { getToken, notifyActivity } = useAuth();
  const { handleUnauthorized } = usePharosAuth();

  useEffect(() => {
    setTokenProvider(getToken);
  }, [getToken]);

  return (
    <ApiClientProvider
      configs={apiConfigs}
      correlationIdPrefix={CORRELATION_ID_PREFIX}
      logger={apiLogger}
      onRequestSent={notifyActivity}
      onUnauthorized={handleUnauthorized}
      tokenProvider={tokenProvider}
    >
      {children}
    </ApiClientProvider>
  );
}

/**
 * Bridges the app-resolved auth context into the shared TelemetryBridge (see PR #484: the
 * hook must be called from the app's module graph, not from inside pharos-lib-web-utils,
 * to avoid duplicate context instances).
 */
function TelemetryAuthBridge({ children }: React.PropsWithChildren): React.JSX.Element {
  const { klareoToken } = usePharosAuth();
  return (
    <TelemetryBridge klareoToken={klareoToken} telemetryClient={telemetryClient}>
      {children}
    </TelemetryBridge>
  );
}

/**
 * Bridges the app-resolved auth context into the shared PendoBridge (same rationale as
 * TelemetryAuthBridge above: the hook must be called from the app's module graph).
 */
function PendoAuthBridge({ children }: React.PropsWithChildren): React.JSX.Element {
  const { klareoToken } = usePharosAuth();
  return (
    <PendoBridge klareoToken={klareoToken} pendoClient={pendoClient}>
      {children}
    </PendoBridge>
  );
}

/**
 * Thin bridge between the app-specific auth context and the library's
 * PermissionsProvider. We keep this wrapper (rather than calling useAuth()
 * inside the library) so that pharos-lib-front-utils stays decoupled from
 * any particular auth implementation.
 */
function AuthenticatedPermissionsProvider({ children }: React.PropsWithChildren): React.JSX.Element {
  const { isAuthenticated } = useAuth();
  return <PermissionsProvider isAuthenticated={isAuthenticated}>{children}</PermissionsProvider>;
}

/**
 * Bridges auth context into RealtimeProvider.
 * Must be mounted inside QueryClientProvider and ApiClientProvider.
 */
function RealtimeProviderBridge({ children }: React.PropsWithChildren): React.JSX.Element {
  const { isAuthenticated, klareoToken } = usePharosAuth();

  const tokenClaims = useMemo((): PharosJWTToken | null => getPharosTokenClaims(klareoToken), [klareoToken]);

  const realtimeConfig = useMemo(() => ({ negotiateUrl: REALTIME_NEGOTIATE_URL }), []);

  return (
    <RealtimeProvider
      apiClientName="messenger"
      config={realtimeConfig}
      enabled={isAuthenticated}
      tenantId={tokenClaims?.tenantId}
      userId={tokenClaims?.userId}
    >
      {children}
    </RealtimeProvider>
  );
}

/** Inner component that has access to auth and permissions contexts and updates router context */
function InnerApp(): React.JSX.Element {
  const auth = useAuth();
  const permissions = usePermissions();

  return (
    <TelemetryErrorBoundary
      isDev={env.isDev}
      onError={(error: Error): void => {
        telemetryClient.trackException(error);
      }}
    >
      <RouterProvider context={{ auth, permissions }} router={router} />
    </TelemetryErrorBoundary>
  );
}

function App(): React.JSX.Element {
  return (
    <AuthProvider>
      <TelemetryAuthBridge>
        <PendoAuthBridge>
          <AuthApiClientBridge>
            <QueryClientProvider client={queryClient}>
              <RealtimeProviderBridge>
                <AuthenticatedPermissionsProvider>
                  <InnerApp />
                </AuthenticatedPermissionsProvider>
              </RealtimeProviderBridge>
            </QueryClientProvider>
          </AuthApiClientBridge>
        </PendoAuthBridge>
      </TelemetryAuthBridge>
    </AuthProvider>
  );
}

export default App;
